import * as React from "react";
import { useEffect, useState } from "react";
import AppointmentDetails from "../booking/AppointmentDetails";
import { fillTemplateString, unescapePathOf } from "../commons/utils/text-rendering";
import sfetch from "../commons/utils/super-fetch";
import * as Sentry from "@sentry/browser";
import AppointmentCard from "../booking/AppointmentCard";
import ModalDelete from "../commons/components/modal/ModalDelete";
import LoadingSpinner from "../flow/utils/LoadingSpinner";

export interface AppointmentUrls {
  fetchAppointmentsUrl: string;
  cancelAppointmentUrl: string;
}

export interface AppointmentsPanelProps extends AppointmentUrls {
  title: string;
}

export function AppointmentsPanel({
  title,
  fetchAppointmentsUrl,
  cancelAppointmentUrl,
}: AppointmentsPanelProps) {
  const [fetchError, setFetchError] = useState<boolean>();
  const [appointmentsDetails, setAppointmentsDetails] = useState<AppointmentDetails[]>();
  const [appointmentDetailsToCancel, setAppointmentDetailsToCancel] =
    useState<AppointmentDetails>();

  const cancelAppointment = async (): Promise<any> => {
    if (appointmentDetailsToCancel && cancelAppointmentUrl) {
      const url = fillTemplateString(unescapePathOf(cancelAppointmentUrl), {
        uid: appointmentDetailsToCancel.patientUid,
      });
      await sfetch(url, {
        method: "DELETE",
        body: JSON.stringify(appointmentDetailsToCancel),
        headers: {
          "Content-Type": "application/json",
        },
      });
    }
  };

  useEffect(() => {
    sfetch(fetchAppointmentsUrl, {
      method: "GET",
    })
      .then((it) => it.json())
      .then((response) => {
        setAppointmentsDetails(response.appointmentsDetails);
        setFetchError(response.error);
      })
      .catch((error) => {
        Sentry.captureException(error);
      });
  }, []);
  return (
    <div id="appointments-panel" className="content panel with-shadow">
      <div className="panel-title">
        <div>{i18n[title]}</div>
      </div>
      {fetchError ? (
        <ErrorMessage />
      ) : (
        <PanelContent
          appointmentsDetails={appointmentsDetails}
          cancelButtonAction={setAppointmentDetailsToCancel}
        />
      )}
      {appointmentDetailsToCancel && (
        <CancelAppointmentPopupModal
          cancelAppointment={cancelAppointment}
          onPopupCancel={() => setAppointmentDetailsToCancel(undefined)}
        />
      )}
    </div>
  );
}

function PanelContent({
  appointmentsDetails,
  cancelButtonAction,
}: {
  appointmentsDetails: AppointmentDetails[];
  cancelButtonAction: (appointmentDetails: AppointmentDetails) => void;
}) {
  return (
    <>
      {appointmentsDetails ? (
        <>
          {appointmentsDetails.length > 0 ? (
            <AppointmentsList
              appointmentsDetails={appointmentsDetails}
              cancelButtonAction={cancelButtonAction}
            />
          ) : (
            <NoAppointment />
          )}
        </>
      ) : (
        <LoadingSpinner />
      )}
    </>
  );
}

function ErrorMessage() {
  return (
    <div className="form-general-error message danger">
      {i18n["Page.Portal.PatientAppointment.Error"]}
    </div>
  );
}

function NoAppointment() {
  return (
    <div className="no-appointment">
      <i className={"ic_calendar"} />
      <div>{i18n["Page.Portal.PatientAppointment.NoAppointment.Description"]}</div>
    </div>
  );
}

function AppointmentsList({
  appointmentsDetails,
  cancelButtonAction,
}: {
  appointmentsDetails: AppointmentDetails[];
  cancelButtonAction: (appointmentDetails: AppointmentDetails) => void;
}) {
  return (
    <>
      {appointmentsDetails.map((appointmentDetails, idxAppointment) => (
        <AppointmentCard
          key={idxAppointment}
          appointmentDetails={appointmentDetails}
          cancelAction={
            appointmentDetails.appointmentId &&
            (appointmentDetails.appointmentId.gatewayAppointmentUid ||
              appointmentDetails.appointmentId.cloud9AppointmentClientUid ||
              appointmentDetails.appointmentId.cloud9AppointmentGUid)
              ? cancelButtonAction
              : undefined
          }
        />
      ))}
    </>
  );
}

function CancelAppointmentPopupModal({
  cancelAppointment,
  onPopupCancel,
}: {
  cancelAppointment: () => Promise<any>;
  onPopupCancel: () => void;
}) {
  const [errorMessage, setErrorMessage] = useState<string>();

  return (
    <ModalDelete
      title={i18n["Page.Portal.PatientAppointment.Popup.Cancel.Title"]}
      actionButtonLabel={i18n["Page.Portal.PatientAppointment.Popup.Cancel.Confirm"]}
      cancelButtonLabel={i18n["Page.Portal.PatientAppointment.Popup.Cancel.Cancel"]}
      cancel={onPopupCancel}
      action={async () => {
        try {
          setErrorMessage(undefined);
          await cancelAppointment();
          document.location.reload();
        } catch (e) {
          setErrorMessage(i18n["Global.TechnicalError"]);
          throw e;
        }
      }}
    >
      {errorMessage && <div className="form-general-error message danger">{errorMessage}</div>}
      {i18n["Page.Portal.PatientAppointment.Popup.Cancel.Content"]}
    </ModalDelete>
  );
}
